import type { WeeklyAccumulation } from './ot-engine';
import type { DailyAdjustedClassification, TimeActivityRecord, PayType } from '../types/payroll-export';
import type { JobAllocation } from '../types/time-record';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface JobPayAllocation {
  readonly jobId: string | null;
  readonly customerId: string | null;
  readonly isPrevailingWage: boolean;
  readonly classificationCode: string | null;
  readonly payType: 'regular' | 'overtime' | 'doubleOvertime';
  readonly minutes: number;
}

export interface PenaltyInput {
  readonly timeRecordId: string;
  readonly employeeRef: string;
  readonly date: string;
  readonly mealBreakPenalty: boolean;
  readonly restBreakPenalty: boolean;
}

export interface ExportableRecord {
  readonly id: string;
  readonly employeeRef: string;
  readonly date: string;
  readonly totalPaidMinutes: number;
  readonly jobAllocations: readonly JobAllocation[];
  readonly breakCompliance: { readonly mealBreakPenalty: boolean; readonly restBreakPenalty: boolean };
  readonly lunchBreakMinutes: number;
  readonly isPrevailingWage: boolean;
  readonly classificationCode: string | null;
  /** Set for time-off records created by onTimeOffRequestWrite. Null/undefined for clock-in records. */
  readonly timeOffType?: 'pto' | 'holiday' | 'unpaid' | null;
}

export interface TimeActivityConfig {
  readonly regular: { readonly qboItemId: string };
  readonly overtime: { readonly qboItemId: string; readonly multiplier: number };
  readonly doubleOvertime: { readonly qboItemId: string; readonly multiplier: number };
  readonly mealBreakPremium: { readonly qboItemId: string };
  readonly restBreakPremium: { readonly qboItemId: string };
  readonly pto?: { readonly qboItemId: string };
  readonly holiday?: { readonly qboItemId: string };
}

/**
 * Distributes weekly overtime back to individual days using reverse-chronological attribution.
 *
 * When the weekly OT method wins over daily OT, the aggregate weekly calculation
 * already knows the total OT minutes. This function reclassifies regular minutes
 * on the most-recent days as OT so that day-level records sum to the correct weekly total.
 *
 * Algorithm:
 * 1. If dailyOtMethod was used, return input unchanged (daily classification is already correct).
 * 2. Compute additionalOt = weeklyOtTotal - dailyOtTotal. If ≤ 0, no adjustment needed.
 * 3. Walk days in reverse chronological order; for each day, reclassify as much regular
 *    time as needed (up to the day's regularMinutes) until the full additionalOt is attributed.
 * 4. Return adjusted array in original (ascending) date order.
 *
 * @param dailyClassifications - Per-day classifications with date, regularMinutes, etc.
 * @param weeklyAccumulation - Weekly totals and metadata including appliedOtMethod.
 * @returns New array of DailyAdjustedClassification with immutably updated values.
 */
export function distributeWeeklyOtToDays(
  dailyClassifications: readonly DailyAdjustedClassification[],
  weeklyAccumulation: WeeklyAccumulation,
): readonly DailyAdjustedClassification[] {
  if (weeklyAccumulation.appliedOtMethod === 'daily') {
    return dailyClassifications;
  }

  const remainingOtToAttribute =
    weeklyAccumulation.weeklyOtTotal - weeklyAccumulation.dailyOtTotal;

  if (remainingOtToAttribute <= 0) {
    return dailyClassifications;
  }

  // Create mutable copy with new objects (immutable pattern)
  const adjusted: DailyAdjustedClassification[] = dailyClassifications.map((d) => ({ ...d }));

  // Sort descending by date for reverse-chronological attribution
  adjusted.sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0));

  let remaining = remainingOtToAttribute;

  for (let i = 0; i < adjusted.length; i++) {
    if (remaining <= 0) break;

    const day = adjusted[i];
    const reclassified = Math.min(remaining, day.regularMinutes);

    adjusted[i] = {
      ...day,
      regularMinutes: day.regularMinutes - reclassified,
      overtimeMinutes: day.overtimeMinutes + reclassified,
    };

    remaining -= reclassified;
  }

  // Restore ascending date order
  adjusted.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  return adjusted;
}

// ---------------------------------------------------------------------------
// allocateOtByJob
// ---------------------------------------------------------------------------

const PAY_TYPE_KEYS = ['regular', 'overtime', 'doubleOvertime'] as const;

/**
 * Maps a DailyAdjustedClassification to the corresponding minutes field name.
 */
function classificationMinutes(
  classification: DailyAdjustedClassification,
  payType: 'regular' | 'overtime' | 'doubleOvertime',
): number {
  switch (payType) {
    case 'regular': return classification.regularMinutes;
    case 'overtime': return classification.overtimeMinutes;
    case 'doubleOvertime': return classification.doubleOvertimeMinutes;
  }
}

/**
 * Adjusts an array of rounded values so that they sum to the desired total.
 * The largest entry absorbs the rounding difference.
 */
function adjustRounding(values: readonly number[], target: number): readonly number[] {
  const sum = values.reduce((a, b) => a + b, 0);
  const diff = target - sum;
  if (diff === 0 || values.length === 0) return values;

  // Find index of largest value
  let largestIdx = 0;
  for (let i = 1; i < values.length; i++) {
    if (values[i] > values[largestIdx]) largestIdx = i;
  }

  return values.map((v, i) => (i === largestIdx ? v + diff : v));
}

/**
 * Splits a day's pay-type minutes across jobs proportionally.
 *
 * @param classification - The adjusted daily OT classification (regular/OT/DT minutes).
 * @param jobAllocations - Job allocations from the time record.
 * @param totalPaidMinutes - Total paid minutes for the day (may exceed allocated job minutes).
 * @returns Flat array of per-job, per-pay-type allocations with minutes.
 */
export function allocateOtByJob(
  classification: DailyAdjustedClassification,
  jobAllocations: readonly JobAllocation[],
  totalPaidMinutes: number,
): readonly JobPayAllocation[] {
  // Zero-job day: one entry per non-zero pay type, null job/customer
  if (jobAllocations.length === 0) {
    return PAY_TYPE_KEYS
      .filter((pt) => classificationMinutes(classification, pt) > 0)
      .map((pt) => ({
        jobId: null,
        customerId: null,
        isPrevailingWage: false,
        classificationCode: null,
        payType: pt,
        minutes: classificationMinutes(classification, pt),
      }));
  }

  const allocatedMinutes = jobAllocations.reduce((sum, j) => sum + j.minutes, 0);

  const results: JobPayAllocation[] = [];

  // For each pay type, distribute proportionally across jobs
  for (const pt of PAY_TYPE_KEYS) {
    const totalForType = classificationMinutes(classification, pt);
    if (totalForType === 0) continue;

    const rawMinutes = jobAllocations.map((j) =>
      Math.round((j.minutes / allocatedMinutes) * totalForType),
    );
    const adjusted = adjustRounding(rawMinutes, totalForType);

    for (let i = 0; i < jobAllocations.length; i++) {
      const mins = adjusted[i];
      if (mins === 0) continue;
      const job = jobAllocations[i];
      results.push({
        jobId: job.jobId,
        customerId: job.customerId,
        isPrevailingWage: job.isPrevailingWage,
        classificationCode: job.classificationCode,
        payType: pt,
        minutes: mins,
      });
    }
  }

  // Handle unallocated time (allocatedMinutes < totalPaidMinutes)
  if (allocatedMinutes < totalPaidMinutes) {
    const unallocatedMinutes = totalPaidMinutes - allocatedMinutes;
    const totalClassifiedMinutes =
      classification.regularMinutes + classification.overtimeMinutes + classification.doubleOvertimeMinutes;

    for (const pt of PAY_TYPE_KEYS) {
      const totalForType = classificationMinutes(classification, pt);
      if (totalForType === 0 || totalClassifiedMinutes === 0) continue;

      const unallocatedForType = Math.round((unallocatedMinutes / totalClassifiedMinutes) * totalForType);
      if (unallocatedForType === 0) continue;

      results.push({
        jobId: null,
        customerId: null,
        isPrevailingWage: false,
        classificationCode: null,
        payType: pt,
        minutes: unallocatedForType,
      });
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// buildPenaltyLines
// ---------------------------------------------------------------------------

/**
 * Creates TimeActivityRecord penalty lines for missed meal/rest breaks.
 * Each penalty is 1 hour at the employee's regular rate.
 *
 * @param records - Records to check for penalty flags.
 * @param baseRates - Map of employeeRef to base hourly rate.
 * @param config - QBO item IDs for meal and rest break premiums.
 * @returns Array of TimeActivityRecord entries for penalties.
 */
export function buildPenaltyLines(
  records: readonly PenaltyInput[],
  baseRates: Readonly<Record<string, number>>,
  config: { mealBreakPremiumItemId: string; restBreakPremiumItemId: string },
): readonly TimeActivityRecord[] {
  const lines: TimeActivityRecord[] = [];

  for (const record of records) {
    if (record.mealBreakPenalty) {
      lines.push({
        employeeRef: record.employeeRef,
        txnDate: record.date,
        hours: 1,
        minutes: 0,
        itemRef: config.mealBreakPremiumItemId,
        customerRef: null,
        hourlyRate: baseRates[record.employeeRef] ?? 0,
        breakHours: 0,
        breakMinutes: 0,
        description: `Meal break premium - ${record.date}`,
        billableStatus: 'NotBillable',
        payType: 'regular',
      });
    }

    if (record.restBreakPenalty) {
      lines.push({
        employeeRef: record.employeeRef,
        txnDate: record.date,
        hours: 1,
        minutes: 0,
        itemRef: config.restBreakPremiumItemId,
        customerRef: null,
        hourlyRate: baseRates[record.employeeRef] ?? 0,
        breakHours: 0,
        breakMinutes: 0,
        description: `Rest break premium - ${record.date}`,
        billableStatus: 'NotBillable',
        payType: 'regular',
      });
    }
  }

  return lines;
}

// ---------------------------------------------------------------------------
// generateTimeActivityLines
// ---------------------------------------------------------------------------

const PAY_TYPE_TO_QBO_PAY_TYPE: Record<'regular' | 'overtime' | 'doubleOvertime', PayType> = {
  regular: 'regular',
  overtime: 'overtime',
  doubleOvertime: 'doubleOvertime',
};

const PAY_TYPE_LABELS: Record<'regular' | 'overtime' | 'doubleOvertime', string> = {
  regular: 'Regular',
  overtime: 'OT',
  doubleOvertime: 'DT',
};

function getMultiplier(
  payType: 'regular' | 'overtime' | 'doubleOvertime',
  config: TimeActivityConfig,
): number {
  switch (payType) {
    case 'regular': return 1.0;
    case 'overtime': return config.overtime.multiplier;
    case 'doubleOvertime': return config.doubleOvertime.multiplier;
  }
}

function getItemId(
  payType: 'regular' | 'overtime' | 'doubleOvertime',
  config: TimeActivityConfig,
): string {
  switch (payType) {
    case 'regular': return config.regular.qboItemId;
    case 'overtime': return config.overtime.qboItemId;
    case 'doubleOvertime': return config.doubleOvertime.qboItemId;
  }
}

/**
 * Orchestrates the full TimeActivity line generation for a set of exportable records.
 *
 * For each record:
 * 1. Looks up the weekly-adjusted classification for the date.
 * 2. Splits minutes across jobs via allocateOtByJob.
 * 3. Builds a TimeActivityRecord for each allocation.
 * 4. Attaches break hours to the regular line with the most minutes.
 * 5. Appends penalty lines for missed meal/rest breaks.
 *
 * @param records - Exportable time records for the period.
 * @param adjustedClassifications - Map of date to weekly-adjusted classification.
 * @param config - Pay type configuration with QBO item IDs and multipliers.
 * @param baseRates - Map of employeeRef to base hourly rate.
 * @returns Complete array of TimeActivityRecord entries.
 */
export function generateTimeActivityLines(
  records: readonly ExportableRecord[],
  adjustedClassifications: Readonly<Record<string, DailyAdjustedClassification>>,
  config: TimeActivityConfig,
  baseRates: Readonly<Record<string, number>>,
): readonly TimeActivityRecord[] {
  const allLines: TimeActivityRecord[] = [];

  for (const record of records) {
    // Time-off records bypass OT classification entirely
    if (record.timeOffType === 'unpaid') {
      continue; // Unpaid time off: no QBO lines
    }
    if (record.timeOffType === 'pto' || record.timeOffType === 'holiday') {
      const totalMinutes = record.totalPaidMinutes;
      if (totalMinutes <= 0) continue;
      const itemId = record.timeOffType === 'pto'
        ? config.pto?.qboItemId
        : config.holiday?.qboItemId;
      if (!itemId) {
        continue; // pto/holiday QBO item ID not configured in taConfig — caller logs the warning
      }
      const label = record.timeOffType === 'pto' ? 'PTO' : 'Holiday';
      allLines.push({
        employeeRef: record.employeeRef,
        txnDate: record.date,
        hours: Math.floor(totalMinutes / 60),
        minutes: Math.round(totalMinutes % 60),
        itemRef: itemId,
        customerRef: null,
        hourlyRate: 0,
        breakHours: 0,
        breakMinutes: 0,
        description: `${label} - ${record.date}`,
        billableStatus: 'NotBillable',
        payType: record.timeOffType,
      });
      continue;
    }

    const classification = adjustedClassifications[record.date];
    if (!classification) continue;

    const jobAllocations = allocateOtByJob(classification, record.jobAllocations, record.totalPaidMinutes);

    // Build description prefix for prevailing wage
    const pwPrefix = record.isPrevailingWage && record.classificationCode
      ? `[PW: ${record.classificationCode}] `
      : '';

    // Find which regular allocation has the most minutes (for break assignment)
    let maxRegularIdx = -1;
    let maxRegularMinutes = -1;

    const recordLines: TimeActivityRecord[] = [];

    for (let i = 0; i < jobAllocations.length; i++) {
      const alloc = jobAllocations[i];
      const baseRate = baseRates[record.employeeRef] ?? 0;
      const multiplier = getMultiplier(alloc.payType, config);
      const label = PAY_TYPE_LABELS[alloc.payType];

      const line: TimeActivityRecord = {
        employeeRef: record.employeeRef,
        txnDate: record.date,
        hours: Math.floor(alloc.minutes / 60),
        minutes: Math.round(alloc.minutes % 60),
        itemRef: getItemId(alloc.payType, config),
        customerRef: alloc.customerId,
        hourlyRate: baseRate * multiplier,
        breakHours: 0,
        breakMinutes: 0,
        description: `${pwPrefix}${label} - ${record.date}`,
        billableStatus: 'NotBillable',
        payType: PAY_TYPE_TO_QBO_PAY_TYPE[alloc.payType],
      };

      recordLines.push(line);

      if (alloc.payType === 'regular' && alloc.minutes > maxRegularMinutes) {
        maxRegularMinutes = alloc.minutes;
        maxRegularIdx = recordLines.length - 1;
      }
    }

    // Attach break hours to the regular line with the most minutes
    if (maxRegularIdx >= 0 && record.lunchBreakMinutes > 0) {
      const target = recordLines[maxRegularIdx];
      recordLines[maxRegularIdx] = {
        ...target,
        breakHours: Math.floor(record.lunchBreakMinutes / 60),
        breakMinutes: Math.round(record.lunchBreakMinutes % 60),
      };
    }

    allLines.push(...recordLines);
  }

  // Build penalty lines
  const penaltyInputs: PenaltyInput[] = records
    .filter((r) => r.breakCompliance.mealBreakPenalty || r.breakCompliance.restBreakPenalty)
    .map((r) => ({
      timeRecordId: r.id,
      employeeRef: r.employeeRef,
      date: r.date,
      mealBreakPenalty: r.breakCompliance.mealBreakPenalty,
      restBreakPenalty: r.breakCompliance.restBreakPenalty,
    }));

  const penaltyLines = buildPenaltyLines(penaltyInputs, baseRates, {
    mealBreakPremiumItemId: config.mealBreakPremium.qboItemId,
    restBreakPremiumItemId: config.restBreakPremium.qboItemId,
  });

  return [...allLines, ...penaltyLines];
}
